import React, { useState, useEffect } from 'react';
import { Check } from 'lucide-react';

const FullHDLogo: React.FC = () => (
  <svg width="52" height="22" viewBox="0 0 52 22" fill="none" xmlns="http://www.w3.org/2000/svg" className="inline-block">
    <rect width="52" height="22" rx="4" fill="#3C59B2" />
    <text x="26" y="15" textAnchor="middle" fill="white" fontSize="9" fontWeight="bold" fontFamily="Arial, sans-serif">FULL HD</text>
  </svg>
);

const products = [
  {
    id: 'matterport-only',
    title: 'Formule 1 : Visite Matterport → 1 vidéo professionnelle de visite virtuelle',
    tabTitle: 'Matterport seul',
    features: ['Envoyez le lien de votre visite Matterport', 'Recevez 1 vidéo professionnelle de visite virtuelle', 'Livraison en 24 h (lun.–jeu.)'],
    useCases: ['Annonces', 'Portails', 'Site web', 'Réseaux'],
    videoId: 'Ar2fLyRiWvY?start=1',
  },
  {
    id: 'matterport-drone',
    title: 'Formule 2 : Matterport + drone → montage vidéo complet',
    tabTitle: 'Matterport + drone',
    features: ['Envoyez votre lien Matterport + vos clips de drone', 'On crée un montage vidéo complet professionnel', 'Livraison en 24 h (lun.–jeu.)'],
    useCases: ['Annonces', 'Portails', 'Site web', 'Réseaux'],
    videoId: 'Wx8lio3JFc4',
  },
];

type ProductShowcaseFRProps = {
  variant?: 'agents' | 'partenaires';
  activeProfileId?: string;
};

const ProductShowcaseFR: React.FC<ProductShowcaseFRProps> = ({ variant = 'partenaires', activeProfileId }) => {
  const [activeTab, setActiveTab] = useState('matterport-only');
  const agentsOnly = variant === 'agents' || variant === 'partenaires';
  const displayProducts = agentsOnly ? products.slice(0, 1) : products;

  // Keep ProductShowcase tabs in sync with the plan selector (ProblemSolutionFR)
  useEffect(() => {
    if (activeProfileId && !agentsOnly) {
      setActiveTab(activeProfileId);
    }
  }, [activeProfileId, agentsOnly]);

  // For agents: driven by parent activeProfileId. For partenaires: driven by internal tab.
  const isDroneSelected = agentsOnly
    ? activeProfileId === 'matterport-drone'
    : activeTab === 'matterport-drone';
  const defaultVideoUrl = 'https://pub-856ba7aa61aa48e7a455614a46820b28.r2.dev/Example.mp4';
  const defaultPosterUrl = 'https://pub-856ba7aa61aa48e7a455614a46820b28.r2.dev/imageref.jpg';
  const droneVideoUrl = 'https://pub-856ba7aa61aa48e7a455614a46820b28.r2.dev/N%C2%B04%20Azalea%20Bay%20Apartments%20copie.mp4';
  const dronePosterUrl = 'https://pub-856ba7aa61aa48e7a455614a46820b28.r2.dev/videoframe_372.png';

  return (
    <section className="py-8 sm:py-12 md:py-16">
      <div className="container mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-8 sm:mb-12 md:mb-16">
          <h2 className="font-manrope text-3xl sm:text-4xl md:text-5xl font-bold text-gray-900 animate-fade-in-up">Ce que vous recevez</h2>
        </div>
        <div className="max-w-7xl mx-auto">
          {!agentsOnly && (
          <div className="mb-6 sm:mb-8 rounded-3xl p-2 bg-white border border-[#E2E8F0]">
            <div className="flex flex-col items-center gap-2 lg:hidden">
              {products.map((product) => (
                <button
                  key={product.id}
                  onClick={() => setActiveTab(product.id)}
                  className={`w-full max-w-md flex items-center justify-between gap-2 px-4 py-3 rounded-full font-medium transition-all duration-300 text-sm ${
                    activeTab === product.id ? 'bg-[#3C59B2] text-white shadow-lg' : 'bg-white border-2 border-[#3C59B2] text-[#3C59B2] hover:bg-slate-50'
                  }`}
                >
                  <span>{variant === 'partenaires' ? (product.id === 'matterport-only' ? 'Je propose Matterport' : 'Je propose Matterport + drone') : product.title}</span>
                </button>
              ))}
            </div>
            <div className="hidden lg:flex justify-center gap-2">
              {products.map((product) => (
                <button
                  key={product.id}
                  onClick={() => setActiveTab(product.id)}
                  className={`flex items-center gap-2 px-4 py-2.5 rounded-full font-medium transition-all duration-300 text-sm ${
                    activeTab === product.id ? 'bg-[#3C59B2] text-white shadow-lg' : 'bg-white border-2 border-[#3C59B2] text-[#3C59B2] hover:bg-slate-50'
                  }`}
                >
                  <span>{variant === 'partenaires' ? (product.id === 'matterport-only' ? 'Je propose Matterport' : 'Je propose Matterport + drone') : product.tabTitle}</span>
                </button>
              ))}
            </div>
          </div>
          )}

          {agentsOnly ? (
            <div className="space-y-6 lg:space-y-8">
              {/* Encart 1 : vidéo Matterport à gauche */}
              <div className="card-hover bg-white border border-[#E2E8F0] rounded-3xl overflow-hidden">
                <div className="flex flex-col lg:flex-row lg:items-center">
                  <div className="w-full lg:w-1/2 p-4 sm:p-5 lg:p-6">
                    <div className="aspect-video rounded-2xl overflow-hidden bg-gray-900">
                      <video
                        className="w-full h-full object-cover rounded-2xl shadow-lg"
                        controls
                        playsInline
                        preload="metadata"
                        poster={defaultPosterUrl}
                        src={defaultVideoUrl}
                      />
                    </div>
                  </div>
                  <div className="w-full lg:w-1/2 p-5 sm:p-6 lg:p-8 flex flex-col gap-5">
                    <h2 className="font-manrope text-2xl sm:text-3xl font-bold text-gray-900 leading-tight text-center">
                      Vidéo Matterport
                    </h2>
                    <ul className="space-y-2.5">
                      <li className="flex items-center gap-3">
                        <Check className="w-5 h-5 text-[#3C59B2] shrink-0" />
                        <span className="text-base sm:text-lg text-gray-700">1 vidéo Full HD à partir de votre Matterport</span>
                      </li>
                      <li className="flex items-center gap-3">
                        <Check className="w-5 h-5 text-[#3C59B2] shrink-0" />
                        <span className="text-base sm:text-lg text-gray-700">
                          {variant === 'partenaires'
                            ? 'Prête à être partager sur les sites, portails et réseaux sociaux de vos clients'
                            : 'Prête à être partager sur votre site, portails et réseaux sociaux'}
                        </span>
                      </li>
                      <li className="flex items-center gap-3">
                        <Check className="w-5 h-5 text-[#3C59B2] shrink-0" />
                        <span className="text-base sm:text-lg text-gray-700">Livrée en 24h (lun.–jeu.)</span>
                      </li>
                    </ul>
                    <div className="flex flex-wrap gap-2">
                      {products[0].useCases.map((useCase, index) => (
                        <span
                          key={index}
                          className="px-3 py-1.5 rounded-full text-sm bg-gray-100 text-gray-600 font-medium"
                        >
                          {useCase}
                        </span>
                      ))}
                    </div>
                  </div>
                </div>
              </div>

              {/* Encart 2 : vidéo Matterport + drone à droite */}
              <div className="card-hover bg-white border border-[#E2E8F0] rounded-3xl overflow-hidden">
                <div className="flex flex-col lg:flex-row-reverse lg:items-center">
                  <div className="w-full lg:w-1/2 p-4 sm:p-5 lg:p-6">
                    <div className="aspect-video rounded-2xl overflow-hidden bg-gray-900">
                      <video
                        className="w-full h-full object-cover rounded-2xl shadow-lg"
                        controls
                        playsInline
                        preload="metadata"
                        poster={dronePosterUrl}
                        src={droneVideoUrl}
                      />
                    </div>
                  </div>
                  <div className="w-full lg:w-1/2 p-5 sm:p-6 lg:p-8 flex flex-col gap-5">
                    <h2 className="font-manrope text-2xl sm:text-3xl font-bold text-gray-900 leading-tight text-center">
                      Vidéo Matterport + clips de drone
                    </h2>
                    <ul className="space-y-2.5">
                      <li className="flex items-center gap-3">
                        <Check className="w-5 h-5 text-[#3C59B2] shrink-0" />
                        <span className="text-base sm:text-lg text-gray-700">1 vidéo Full HD : Vidéo Matterport et clips de drone assemblés en un seul montage</span>
                      </li>
                      <li className="flex items-center gap-3">
                        <Check className="w-5 h-5 text-[#3C59B2] shrink-0" />
                        <span className="text-base sm:text-lg text-gray-700">
                          {variant === 'partenaires'
                            ? 'Prête à être partager sur les sites, portails et réseaux sociaux de vos clients'
                            : 'Prête à être partager sur votre site, portails et réseaux sociaux'}
                        </span>
                      </li>
                      <li className="flex items-center gap-3">
                        <Check className="w-5 h-5 text-[#3C59B2] shrink-0" />
                        <span className="text-base sm:text-lg text-gray-700">Livrée en 24h (lun.–jeu.)</span>
                      </li>
                    </ul>
                    <div className="flex flex-wrap gap-2">
                      {products[1].useCases.map((useCase, index) => (
                        <span
                          key={index}
                          className="px-3 py-1.5 rounded-full text-sm bg-gray-100 text-gray-600 font-medium"
                        >
                          {useCase}
                        </span>
                      ))}
                    </div>
                  </div>
                </div>
              </div>
            </div>
          ) : (
            <div className="card-hover bg-white border border-[#E2E8F0] rounded-3xl overflow-hidden">
              {displayProducts.map((product) => (
                <div key={product.id} className={activeTab === product.id ? 'block' : 'hidden'}>
                  <div className="flex flex-col lg:flex-row lg:items-center">
                    <div className="w-full lg:w-[52%] p-4 sm:p-5 lg:p-6">
                      <div className="aspect-video rounded-2xl overflow-hidden bg-gray-900">
                        <video
                          key={isDroneSelected ? 'drone' : 'default'}
                          className="w-full h-full object-cover rounded-2xl shadow-lg"
                          controls
                          playsInline
                          preload="metadata"
                          poster={isDroneSelected ? dronePosterUrl : defaultPosterUrl}
                          src={isDroneSelected ? droneVideoUrl : defaultVideoUrl}
                        />
                      </div>
                    </div>
                    <div className="w-full lg:w-[48%] p-5 sm:p-6 lg:p-8 flex flex-col gap-5">
                      <div>
                        <h3 className="font-manrope text-xl sm:text-2xl font-bold text-gray-900 leading-tight">{product.title}</h3>
                        <div className="mt-3"><FullHDLogo /></div>
                      </div>
                      <ul className="space-y-2.5">
                        {product.features.map((feature, index) => (
                          <li key={index} className="flex items-center gap-3">
                            <div className="w-2 h-2 rounded-full bg-[#3C59B2] shrink-0" />
                            <span className="text-base sm:text-lg text-gray-700">{feature}</span>
                          </li>
                        ))}
                      </ul>
                      <div className="flex flex-wrap gap-2">
                        {product.useCases.map((useCase, index) => (
                          <span key={index} className="px-3 py-1.5 rounded-full text-sm bg-gray-100 text-gray-600 font-medium">{useCase}</span>
                        ))}
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </section>
  );
};

export default ProductShowcaseFR;
